import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import { IGetDishPortionInfo, TLanguageIDs } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getDishPortionInfo } from 'backend/dishes';
import { IAppContext, AppContext } from 'appState/appContext';
import { viewPortionInitialState, ViewPortionContext } from './viewPortionContext';
import { viewPortionReducer } from './viewPortionReducer';
import { ViewPortionActionTypes } from './viewPortionActions';
import { IViewDishContext, ViewDishContext } from '../../../../viewDishState/viewDishContext';

export const ViewPortionState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(viewPortionReducer, viewPortionInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { selectedPortionId } = useContext<IViewDishContext>(ViewDishContext);
	const { response, loading } = useFetchData<IGetDishPortionInfo, string>(
		selectedPortionId.length ? getDishPortionInfo : null,
		selectedPortionId,
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: ViewPortionActionTypes.SET_DATA,
				payload: {
					portionFullInfo: response.data,
				},
			});
		}
	}, [response]);

	useEffect(() => {
		if (state.portionModifierGroupId) {
			dispatch({
				type: ViewPortionActionTypes.SET_PORTION_MODIFIER_GROUP_MODIFIERS,
				payload: {
					portionModifierGroupModifiers:
						state.portionFullInfo?.modifierGroups.find((g) => g.id === state.portionModifierGroupId)
							?.modifiers ?? [],
				},
			});
		}
	}, [state.portionModifierGroupId]);

	const setTranslationTab = useCallback((value: Nullable<TLanguageIDs>) => {
		dispatch({
			type: ViewPortionActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const setPortionModifierGroupId = useCallback((value: string) => {
		dispatch({
			type: ViewPortionActionTypes.SET_PORTION_MODIFIER_GROUP_ID,
			payload: {
				portionModifierGroupId: value,
				portionModifierGroupModifiers: value
					? state.portionFullInfo?.modifierGroups.find((g) => g.id === value)?.modifiers ?? []
					: [],
			},
		});
	}, []);

	return (
		<ViewPortionContext.Provider
			value={{
				setTranslationTab,
				setPortionModifierGroupId,
				portionFullInfo: state.portionFullInfo,
				portionModifierGroupId: state.portionModifierGroupId,
				portionModifierGroupModifiers: state.portionModifierGroupModifiers,
				selectedTranslationTab: state.selectedTranslationTab,
			}}>
			{children}
		</ViewPortionContext.Provider>
	);
};
